/**
 * React Query retry-guard — QA-132 / Q-T351.
 *
 * A React Query alapértelmezése `retry: 3`, ami a **`4xx`-eket is** újrapróbálja.
 * A `429` (rate limit) esetén ez aktívan káros: egy dashboard-betöltés `33` API-hívás,
 * a BE limitje `100 / perc / user` (`app.module.ts`), így a 3. betöltés után jövő `429`
 * `4×` fut le, backoff-fal `~14 s`-ig függőben marad, és a `networkidle` sosem áll be.
 *
 * A `4xx` szemantikailag sem retry-alany: a kérés a kliens hibája, az ismétlés
 * ugyanazt adja vissza. Az `5xx` és a hálózati hiba **marad** újrapróbálható.
 *
 * ⚠️ Hatókör: a predikátum a hibaobjektum **strukturált** státuszmezőjéből olvas
 * (`ApiError.statusCode` · `OfflineLayerHttpError.status` · `response.status`).
 * Szöveges `Error('HTTP 429')` alakot **NEM** parse-ol — ott a státusz nem adat, és a
 * szöveg-illesztés némán romlana el.
 *
 * 🔴 **Ez a korlát EGYSZER MÁR MEGFOGOTT** (`QA-132` re-verify, 2026-08-31): a `429` a GET-úton
 * pontosan ilyen szöveges hibaként érkezett (`offline-api-layer.ts`), ezért a guard a bundle-ben
 * bent volt, de futásidőben hatástalan maradt. A javítás a **forrásnál** történt (minden nem-ok
 * státusz strukturáltan dobódik), nem itt. ⇒ **aki új dobási helyet ad hozzá, strukturált
 * státusszal dobjon** — a predikátum nem fogja megvédeni.
 */

/** A hibaobjektum HTTP-státusza, ha strukturáltan hordozza. */
export function getErrorStatus(error: unknown): number | undefined {
  if (!error || typeof error !== 'object') return undefined;

  const candidate = error as {
    statusCode?: unknown;
    status?: unknown;
    response?: { status?: unknown } | null;
  };

  for (const value of [candidate.statusCode, candidate.status, candidate.response?.status]) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }

  return undefined;
}

/** `true`, ha a hiba `4xx` — vagyis az újrapróbálás értelmetlen. */
export function isClientError(error: unknown): boolean {
  const status = getErrorStatus(error);
  return status !== undefined && status >= 400 && status < 500;
}

/**
 * Retry-predikátum legfeljebb `max` újrapróbálással, `4xx`-en azonnali feladással.
 * Ugyanaz az alak, mint a numerikus `retry: <n>`, csak a `4xx` ki van zárva belőle.
 */
export function retryUpTo(max: number): (failureCount: number, error: Error) => boolean {
  // ⚠️ A parameter tipusa `Error`, NEM `unknown`: a React Query a `retry`-predikatumbol
  // is inferalja a `TError`-t, es egy `unknown`-parameteru fuggveny `TError = {}`-ra viszi
  // — amitol a hivo oldalan a `query.error?.message` fordulasi hibat ad (TS2339).
  return (failureCount: number, error: Error): boolean => {
    if (isClientError(error)) return false;
    return failureCount < max;
  };
}

/** A `QueryClient` globális alapértelmezése — az RQ `retry: 3`-jával azonos, `4xx`-guarddal. */
export const defaultQueryRetry = retryUpTo(3);
